import React, { useState, useEffect } from 'react';
import { XMarkIcon, PlusIcon, CheckIcon } from '@heroicons/react/24/outline';
import { APIURL } from '../../../configApi/apiConfig';
import axios from '../../../configApi/axiosConfig';

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

/** Convierte una fecha ISO a YYYY-MM-DD; si no hay fecha usa hoy. */
const toDateString = (fechaISO) =>
    fechaISO
        ? new Date(fechaISO).toISOString().split('T')[0]
        : new Date().toISOString().split('T')[0];

/** Crea un objeto de teléfono ya guardado (solo lectura). */
const makePhone = (id, key, telefono, descripcion, fecha, guardado = true) => ({
    id,
    idCre_GCTelefono: key,
    telefono,
    descripcion,
    fecha: toDateString(fecha),
    guardado
});

/** Crea una fila vacía lista para editar. */
const makeEmptyPhone = (id) => ({
    id,
    idCre_GCTelefono: null,
    telefono: '',
    descripcion: '',
    fecha: new Date().toISOString().split('T')[0],
    guardado: false
});

// ─────────────────────────────────────────────────────────────────────────────
// Componente principal
// ─────────────────────────────────────────────────────────────────────────────
export function TelefonosNuevos({ isOpen, onClose, onSave, clienteData, data, item, isInline = false }) {
    const [telefonos, setTelefonos]               = useState([]);
    const [telefonosGuardados, setTelefonosGuardados] = useState([]);

    // ── Carga inicial: combina teléfonos de múltiples fuentes ─────────────
    useEffect(() => {
        const fetchTelefonos = async () => {
            const cliente   = data || clienteData || item || {};
            const phones    = [];
            let   counter   = 1;

            // Teléfonos estáticos del objeto cliente
            const staticPhones = [
                { value: cliente.Celular,            label: 'Celular' },
                { value: cliente.TelefonoDomicilio,  label: 'Teléfono Domicilio' },
                { value: cliente.TelefonoDomiliarDos, label: 'Teléfono Secundario' }
            ];
            staticPhones.forEach(({ value, label }) => {
                if (value) phones.push(makePhone(counter++, `TELF_INIT_${counter}`, value, label, null));
            });

            // Resolver idCliente si no viene en los datos
            const idCliente = cliente.idCliente || data?.idCliente || item?.idCliente || 0;
            const cedula    = cliente.Cedula    || data?.Cedula    || item?.Cedula    || '';
            const solicitud = cliente.sCre_SolicitudWeb || data?.sCre_SolicitudWeb || item?.sCre_SolicitudWeb || '';

            let finalIdCliente = idCliente;
            if (!finalIdCliente && cedula) {
                try {
                    const res = await axios.get(APIURL.getCliente(cedula));
                    if (res.data?.success && res.data?.data?.idCliente) {
                        finalIdCliente = res.data.data.idCliente;
                    }
                } catch (error) {
                    console.error('Error fetching idCliente:', error);
                }
            }

            // Teléfonos guardados en BD (cre_GCTelefono)
            if (finalIdCliente) {
                try {
                    const response = await axios.get(APIURL.get_creGcTelefonoByCliente(finalIdCliente, cedula));
                    (response.data || []).forEach((h) => {
                        phones.push(makePhone(
                            counter++,
                            `TELF_DB_${h.idCre_GCTelefono}`,
                            h.Telefono,
                            h.Descripcion || 'Extra',
                            h.FechaSistema
                        ));
                    });
                } catch (error) {
                    console.error('Error fetching teléfonos históricos:', error);
                }
            }

            // Teléfonos de verificación telefónica (maestro)
            if (solicitud) {
                try {
                    const res = await axios.get(APIURL.get_cre_verificacion_telefonica_maestro(solicitud, 11));
                    const maestro = Array.isArray(res.data) ? res.data
                        : Array.isArray(res.data?.data)    ? res.data.data
                        : [];

                    maestro.forEach((h) => {
                        const existe = phones.some((p) => p.telefono === h.telefono);
                        if (!existe) {
                            phones.push(makePhone(
                                counter++,
                                `TELF_MAESTRO_${counter}`,
                                h.telefono,
                                h.observacion || h.estadoGestns || 'Gestión Telefónica',
                                h.FechaSistema
                            ));
                        }
                    });
                } catch (error) {
                    console.error('Error fetching teléfonos maestro:', error);
                }
            }

            // Siempre tener al menos una fila editable si no hay datos
            const lista = phones.length > 0 ? phones : [makeEmptyPhone(1)];
            setTelefonos(lista);
            setTelefonosGuardados(lista.filter((p) => p.guardado));
        };

        fetchTelefonos();
    }, [data, clienteData, item]);

    // ── Handlers ──────────────────────────────────────────────────────────
    const handleChangeTelefono = (index, field, value) => {
        setTelefonos((prev) => {
            const next = [...prev];
            next[index] = { ...next[index], [field]: value };
            return next;
        });
    };

    const handleAgregarFila = () => {
        setTelefonos((prev) => [...prev, makeEmptyPhone(prev.length + 1)]);
    };

    const handleGuardarFila = (index) => {
        const telefono = telefonos[index];
        if (!telefono.telefono.trim()) {
            alert('El teléfono es requerido');
            return;
        }
        setTelefonos((prev) => {
            const next     = [...prev];
            next[index]    = { ...next[index], idCre_GCTelefono: `TELF_${Date.now()}_${index}`, guardado: true };
            setTelefonosGuardados((g) => [...g, next[index]]);
            return next;
        });
    };

    const handleGuardar = () => {
        const listos = telefonos
            .map((t, i) =>
                t.telefono.trim() && !t.guardado
                    ? { ...t, idCre_GCTelefono: t.idCre_GCTelefono || `TELF_${Date.now()}_${i}`, guardado: true }
                    : t
            )
            .filter((t) => t.telefono.trim() && t.guardado);

        setTelefonos(listos);
        setTelefonosGuardados(listos);

        if (typeof onSave === 'function') {
            onSave(listos);
        } else {
            console.warn("La propiedad 'onSave' no fue pasada al componente TelefonosNuevos.");
        }
        onClose();
    };

    if (!isOpen) return null;

    // ── Estilos reutilizables ─────────────────────────────────────────────
    const inputClass = (disabled) =>
        `w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 
        focus:border-transparent text-sm ${disabled ? 'bg-gray-100 cursor-not-allowed' : ''}`;

    // ── Render ────────────────────────────────────────────────────────────
    const content = (
        <div className={`bg-white shadow-2xl flex flex-col ${!isInline ? 'rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden' : 'w-full'}`}>

            {/* Header — solo visible en modo modal */}
            {!isInline && (
                <div className="bg-gradient-to-r from-blue-700 via-blue-600 to-cyan-600 px-6 py-5 flex items-center justify-between">
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                    >
                        <XMarkIcon className="w-6 h-6 text-white" />
                    </button>
                </div>
            )}

            {/* Tabla de teléfonos */}
            <div className="flex-1 overflow-auto p-6 bg-gray-50">
                <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-200">
                    <table className="w-full">
                        <thead>
                            <tr className="bg-gradient-to-r from-blue-700 to-blue-600 text-white sticky top-0">
                                {['Teléfono', 'Descripción', 'Fecha', 'Acciones'].map((col, i) => (
                                    <th
                                        key={col}
                                        className={`px-4 py-3 text-sm font-bold uppercase tracking-wider ${i === 3 ? 'text-center' : 'text-left'}`}
                                    >
                                        {col}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                            {telefonos.map((tel, index) => (
                                <tr
                                    key={index}
                                    className={`${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} ${tel.guardado ? 'bg-green-50' : ''}`}
                                >
                                    <td className="px-4 py-3">
                                        <input
                                            type="tel"
                                            value={tel.telefono}
                                            onChange={(e) => handleChangeTelefono(index, 'telefono', e.target.value)}
                                            disabled={tel.guardado}
                                            placeholder="Ej: +593 9 98765432"
                                            className={inputClass(tel.guardado)}
                                        />
                                    </td>
                                    <td className="px-4 py-3">
                                        <input
                                            type="text"
                                            value={tel.descripcion}
                                            onChange={(e) => handleChangeTelefono(index, 'descripcion', e.target.value)}
                                            disabled={tel.guardado}
                                            placeholder="Ej: Celular principal"
                                            className={inputClass(tel.guardado)}
                                        />
                                    </td>
                                    <td className="px-4 py-3">
                                        <input
                                            type="date"
                                            value={tel.fecha}
                                            onChange={(e) => handleChangeTelefono(index, 'fecha', e.target.value)}
                                            disabled={tel.guardado}
                                            className={inputClass(tel.guardado)}
                                        />
                                    </td>
                                    <td className="px-4 py-3 text-center">
                                        {!tel.guardado ? (
                                            <button
                                                onClick={() => handleGuardarFila(index)}
                                                className="p-2 hover:bg-green-100 rounded-lg transition-colors group"
                                                title="Guardar fila"
                                            >
                                                <CheckIcon className="w-4 h-4 text-green-600 group-hover:text-green-700" />
                                            </button>
                                        ) : (
                                            <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-semibold rounded">
                                                Guardado
                                            </span>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <button
                    onClick={handleAgregarFila}
                    className="mt-4 px-4 py-2.5 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800
                               text-white font-semibold rounded-lg shadow-md hover:shadow-lg transition-all flex items-center gap-2"
                >
                    <PlusIcon className="w-5 h-5" />
                    Agregar Fila
                </button>
            </div>

            {/* Footer */}
            <div className="bg-gray-100 px-6 py-4 flex items-center justify-between border-t border-gray-200">
                <div className="text-sm font-semibold text-gray-700">
                    Guardados: <span className="text-green-600">{telefonosGuardados.length}</span>
                </div>
                <div className="flex items-center gap-3">
                    <button
                        onClick={onClose}
                        className="px-6 py-2.5 bg-gray-300 hover:bg-gray-400 text-gray-800 font-semibold rounded-lg shadow-md hover:shadow-lg transition-all"
                    >
                        Cancelar
                    </button>
                    <button
                        onClick={handleGuardar}
                        disabled={telefonosGuardados.length === 0}
                        className={`px-6 py-2.5 font-semibold rounded-lg shadow-md hover:shadow-lg transition-all
                            ${telefonosGuardados.length > 0
                                ? 'bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white'
                                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                            }`}
                    >
                        Guardar ({telefonosGuardados.length})
                    </button>
                </div>
            </div>
        </div>
    );

    return isInline ? content : (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-fadeIn">
            {content}
        </div>
    );
}
